<template>
	<div class="container py-4" :key="userStore.uuid">
		<div class="d-flex justify-content-between align-items-center mb-3">
			<h2>Week's Meal Plan</h2>
			<div
				class="text-end"
				v-if="
					!userStore.isViewingUser() &&
					weeklyMealPlan &&
					Object.keys(weeklyMealPlan).length &&
					tokenStore.hasToken()
				">
				<button
					class="btn btn-outline-warning btn-sm me-2"
					@click="fetchGroceryList"
					:disabled="!isOnline">
					<i class="bi bi-cart4"></i> Grocery List
				</button>
				<button
					class="btn btn-outline-light btn-sm"
					@click="editWeek"
					:disabled="!isOnline">
					<i class="bi bi-pencil-square"></i> Edit Week
				</button>
			</div>
		</div>

		<!-- Display if there are no meals planned -->
		<glass-card
			v-if="
				(!weeklyMealPlan ||
					(weeklyMealPlan && !Object.keys(weeklyMealPlan).length)) &&
				!loading &&
				!isPolling
			"
			class="d-flex flex-column justify-content-center align-items-center">
			<div>
				<div v-if="tokenStore.hasToken()">
					<div class="text-center mb-3">
						<h4>No Meal Plans Available</h4>
						<p v-if="!userStore.isViewingUser()">
							Describe any specific preferences, days you'd like to go out to
							eat, or types of food you're interested in.
						</p>
						<div v-if="errorMessage" class="alert alert-danger">
							{{ errorMessage }}
						</div>
					</div>
					<!-- <div class="form-check mb-3">
					<input
						type="checkbox"
						class="form-check-input"
						id="leftOvers"
						v-model="leftOvers" />
					<label class="form-check-label" for="leftOvers">Left Overs?</label>
				</div> -->
					<!-- <div class="mb-3">
						<label for="cookForNumPeople" class="form-label"
							>Cooking for how many people?</label
						>
						<input
							type="number"
							class="form-control"
							id="cookForNumPeople"
							v-model="cookForNumPeople" />
					</div> -->
					<div v-if="!userStore.isViewingUser()">
						<MealPlanSettings v-model="mealPlanSettings" transparent="true" />
						<div class="text-center mb-3">
							<button
								class="btn btn-lg btn-primary m-5"
								@click="submitNoMealsPrompt"
								:disabled="!isOnline">
								Submit
							</button>
						</div>
					</div>
				</div>
				<div v-else>
					<div class="text-center mb-3">
						<h4>No Meal Plans Available</h4>
						<p>You need to be signed in, in order to create one.</p>
					</div>
				</div>
			</div>
		</glass-card>

		<!-- Workflow Status Section -->
		<div v-if="isPolling" class="text-center my-5">
			<div class="spinner-border text-primary mb-3" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			<h4>Creating Your Personalized Meal Plan</h4>
			<p>Please wait while we craft your perfect menu...</p>
			<div v-if="workflowStatus" class="mt-3">
				<p>Status: {{ workflowStatus }}</p>
			</div>
		</div>

		<!-- Inline Edit Area for Week -->
		<div v-if="showWeekEdit" class="mb-4">
			<textarea
				class="form-control mb-2"
				v-model="weekEditContent"
				placeholder="Edit the entire week..."></textarea>
			<button
				class="btn btn-primary btn-sm me-2"
				@click="submitWeekEdit"
				:disabled="!isOnline">
				Submit
			</button>
			<button
				class="btn btn-secondary btn-sm"
				@click="cancelWeekEdit"
				:disabled="!isOnline">
				Cancel
			</button>
		</div>

		<!-- Daily Meal Plans -->
		<div class="d-flex flex-wrap flex-row gap-5">
			<div class="mb-4" v-for="(dayMeals, date) in weeklyMealPlan" :key="date">
				<MealPlanCard
					:class="{ 'current-day': isToday(date) }"
					:title="date"
					:meals="dayMeals"
					:viewing-member="userStore.isViewingUser()"
					:isEditable="tokenStore.hasToken()"
					@edit="(prompt) => editDay(date, prompt)" />
			</div>
		</div>

		<div v-if="loading" class="loading-overlay">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			<h5 class="ms-3">Fetching data...</h5>
		</div>

		<GroceryListDialog
			v-if="showGroceryListDialog"
			:list="groceryList"
			@close="showGroceryListDialog = false" />
	</div>
</template>

<script setup>
	import { ref, onMounted, watch, onUnmounted } from "vue"
	import moment from "moment-timezone"
	import MealPlanCard from "@/components/MealPlanCard.vue"
	// import mockData from "@/utils/mockData"
	import { startLoading, stopLoading } from "@/utils/loaderBus"
	import { useTokenStore } from "@/stores/token"
	import GroceryListDialog from "@/components/GroceryListDialog.vue"
	// import { useRouter } from "vue-router"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import Swal from "sweetalert2"
	import { useUserStore } from "@/stores/user"
	import { isOnline } from "@/utils/networkBus"
	import { Capacitor } from "@capacitor/core"
	import MealPlanSettings from "@/components/MealPlanSettings.vue"
	import getURL from "@/utils/url"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"
	import GlassCard from "@/components/GlassCard.vue"

	const tokenStore = useTokenStore()
	// const router = useRouter()

	const userStore = useUserStore()

	const weeklyMealPlan = ref({})
	const mealPlanId = ref(null)
	const showWeekEdit = ref(false)
	const weekEditContent = ref("")
	// const leftOvers = ref(true)
	const loading = ref(false)
	// const cookForNumPeople = ref(1)

	const groceryList = ref([])
	const showGroceryListDialog = ref(false)

	// Add new refs for workflow handling
	const isPolling = ref(false)
	const workflowStatus = ref("")
	const pollInterval = ref(null)

	// Add new ref for error message
	const errorMessage = ref("")

	const mealPlanSettings = ref({
		timesToEatOut: "",
		dietDescription: "",
		dislikes: "",
		likes: "",
		additionalInfo: "",
		cookForNumPeople: 1,
	})

	const isToday = (date) => {
		return moment().format("YYYY-MM-DD") === date
	}

	const editWeek = () => {
		showWeekEdit.value = true
	}

	const submitWeekEdit = async () => {
		try {
			startLoading()
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth(
				"/api/edit-meal-plan/" + mealPlanId.value,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						"User-Timezone": tz,
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({
						editKey: "$",
						prompt: weekEditContent.value,
					}),
				}
			)
			stopLoading()
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			const data = await response.json()
			console.log(data)
			weeklyMealPlan.value = data.entry
			weekEditContent.value = ""
			// }
		} catch (error) {
			console.error("Error:", error)
		} finally {
			stopLoading()
		}

		showWeekEdit.value = false
	}

	const cancelWeekEdit = () => {
		showWeekEdit.value = false // Simply hide the edit area without saving changes
		weekEditContent.value = "" // Optionally clear the content
	}

	const startPolling = (workflowId) => {
		if (!workflowId) return

		isPolling.value = true
		errorMessage.value = "" // Clear any previous error messages
		localStorage.setItem("mealPlanWorkflowId", workflowId)

		const poll = async () => {
			if (!isOnline.value) {
				return // Don't poll if offline
			}

			// Decide your base URL
			let baseUrl = getURL("add-meal")
			const { hostname } = location
			// if (
			// 	!/grubvision\.com/i.test(hostname) ||
			// 	(Capacitor.isNativePlatform() && Capacitor.DEBUG)
			// ) {
			// 	baseUrl = "http://192.168.0.93:8789"
			// } else {
			// baseUrl = "https://add-meal-plan.grubvision.com"
			// }

			try {
				const response = await fetch(`${baseUrl}status?id=${workflowId}`)
				const data = await response.json()

				workflowStatus.value = data.status

				if (data.status === "complete") {
					if (data.output && data.output.id && data.output.entry) {
						weeklyMealPlan.value = data.output.entry
						mealPlanId.value = data.output.id
					} else {
						// Handle case where status is complete but no valid output
						errorMessage.value =
							"There was an issue creating your meal plan. Please try again."
					}
					// Clean up polling in both success and error cases
					isPolling.value = false
					clearInterval(pollInterval.value)
					localStorage.removeItem("mealPlanWorkflowId")
				} else if (data.status === "errored") {
					errorMessage.value =
						"There was an issue creating your meal plan. Please try again."
					isPolling.value = false
					clearInterval(pollInterval.value)
					localStorage.removeItem("mealPlanWorkflowId")
					await fetchWeekData() //just incase
				}
			} catch (error) {
				console.error("Polling error:", error)
				errorMessage.value =
					"There was an issue creating your meal plan. Please try again."
				isPolling.value = false
				clearInterval(pollInterval.value)
				localStorage.removeItem("mealPlanWorkflowId")
				await fetchWeekData() //just incase
			}
		}

		// Poll every 10 seconds
		pollInterval.value = setInterval(poll, 10000)
		// Initial poll
		poll()
	}

	const stopPolling = () => {
		if (pollInterval.value) {
			clearInterval(pollInterval.value)
			pollInterval.value = null
		}
		isPolling.value = false
		errorMessage.value = "" // Clear error message when stopping
	}

	const submitNoMealsPrompt = async () => {
		const feature = "Meal Planning"
		if (SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
			return
		}

		try {
			startLoading()
			errorMessage.value = "" // Clear any previous error messages
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth("/api/add-meal-plan", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"User-Timezone": tz,
				},
				body: JSON.stringify(mealPlanSettings.value),
			})

			if (
				!(await SubscriptionHelper.requestCheckSubscription(response, feature))
			) {
				stopLoading()
				return
			}

			const data = await response.json()

			if (data.id) {
				startPolling(data.id)
			}
		} catch (error) {
			console.error("Error:", error)
			alert("Cannot create meal plan")
		} finally {
			stopLoading()
		}
	}

	// Function to handle editing a specific day
	const editDay = async (date, prompt) => {
		try {
			startLoading()
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth(
				"/api/edit-meal-plan/" + mealPlanId.value,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						"User-Timezone": tz,
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({
						editKey: date,
						prompt: prompt,
					}),
				}
			)
			stopLoading()
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			const data = await response.json()
			console.log(data)
			weeklyMealPlan.value = data.entry
			// }
		} catch (error) {
			console.error("Error:", error)
		} finally {
			stopLoading()
		}
	}

	// // Function to generate mock data for the week
	// const generateMockData = () => {
	// 	const today = moment()
	// 	// const endOfWeek = moment().endOf("week").endOf("day")
	// 	const endOfWeek = moment().weekday(7)

	// 	for (const d = moment(today); d <= endOfWeek; d.add(1, "days")) {
	// 		const dateStr = d.format("YYYY-MM-DD")
	// 		weeklyMealPlan[dateStr] = mockData
	// 	}
	// }

	const fetchGroceryList = async () => {
		try {
			startLoading()
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth(
				`/api/grocery_list/${mealPlanId.value}`,
				{
					headers: {
						"Content-Type": "application/json",
						"User-Timezone": tz,
						// Authorization: `Bearer ${tokenStore.token}`,
					},
				}
			)
			stopLoading()
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (response.ok) {
				const data = await response.json()
				groceryList.value = data
				showGroceryListDialog.value = true
			} else {
				throw new Error("Failed to fetch grocery list")
			}
			// }
		} catch (error) {
			console.error("Error:", error)
			Swal.fire({
				icon: "error",
				title: "Could not generate list. Try again later.",
			})
		} finally {
			stopLoading()
		}
	}

	const fetchWeekData = async () => {
		try {
			loading.value = true
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth("/api/meal-plan", {
				method: "GET",
				headers: {
					"User-Timezone": tz,
					// Authorization: `Bearer ${tokenStore.token}`,
				},
			})
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			const data = await response.json()
			// console.log(data)
			const today = moment().format("YYYY-MM-DD")

			if (data && data.entry) {
				for (const date in data.entry) {
					if (moment(date).isBefore(today)) {
						delete data.entry[date]
					}
				}

				if (data.id) {
					errorMessage.value = null
					weeklyMealPlan.value = data.entry
					mealPlanId.value = data.id
				}
			}

			// if (Object.keys(weeklyMealPlan.value).length === 0) {
			// 	fetchProfile()
			// }
			// }
		} catch (error) {
			console.error("Error:", error)
		} finally {
			loading.value = false
		}
	}

	// Watch for online status changes
	watch(
		() => isOnline.value,
		(online) => {
			const savedWorkflowId = localStorage.getItem("mealPlanWorkflowId")
			if (online && savedWorkflowId && !pollInterval.value) {
				startPolling(savedWorkflowId)
			}
		}
	)

	// Check for existing workflow on mount
	onMounted(() => {
		const savedWorkflowId = localStorage.getItem("mealPlanWorkflowId")
		if (savedWorkflowId) {
			startPolling(savedWorkflowId)
		}
		fetchWeekData()
	})

	// Clean up on component unmount
	onUnmounted(() => {
		stopPolling()
	})

	watch(
		() => userStore.user,
		(v) => {
			if (v) {
				mealPlanSettings.value = {
					// ...mealPlanSettings.value,
					timesToEatOut: v.timesToEatOut || "",
					likes: v.likes || "",
					dislikes: v.dislikes || "",
					additionalInfo: v.additionalInfo || "",
					dietDescription: v.dietDescription || "",
					cookForNumPeople: v.cookForNumPeople || 1,
				}
			}
		},
		{ immediate: true }
	)
</script>

<style lang="less" scoped>
	.bi-pencil-square {
		cursor: pointer;
	}

	.current-day > ::v-deep(.meal-plan) {
		background-color: #d1e7ff !important; /* More vibrant blue background */
		border: 2px solid #0d6efd; /* Blue border for emphasis */
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.1) !important;
		transform: scale(1.02); /* Slightly scale up for a popping effect */
		transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out; /* Smooth transition for interactive feedback */
	}

	.loading-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		// background: rgba(255, 255, 255, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
	}
</style>
